import { useMemo, useState } from 'react';

import { Button, Group, Modal, Radio, Stack, Text, TextInput } from '@mantine/core';

type SnoozePreset = '1-hour' | 'tomorrow-morning' | 'next-week' | 'custom';

interface SnoozeDialogProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: (wakeAt: Date, reason: string | null) => Promise<void> | void;
  /** Codename + title rendered in the modal header for context. */
  itemTitle?: string;
}

/**
 * Compute the wake-at moment for a preset.
 *
 * - `1-hour`: now + 1 hour.
 * - `tomorrow-morning`: 09:00 local time, next calendar day.
 * - `next-week`: 09:00 local time, next Monday.
 * - `custom`: the user-typed datetime; caller validates non-null.
 */
function wakeAtForPreset(preset: SnoozePreset, customIso: string): Date | null {
  const now = new Date();
  switch (preset) {
    case '1-hour':
      return new Date(now.getTime() + 60 * 60 * 1000);
    case 'tomorrow-morning': {
      const d = new Date(now);
      d.setDate(d.getDate() + 1);
      d.setHours(9, 0, 0, 0);
      return d;
    }
    case 'next-week': {
      const d = new Date(now);
      // Days to next Monday — if today's Monday, jump a full week.
      const todayDow = d.getDay();
      const daysUntilMonday = (1 - todayDow + 7) % 7 || 7;
      d.setDate(d.getDate() + daysUntilMonday);
      d.setHours(9, 0, 0, 0);
      return d;
    }
    case 'custom':
      if (!customIso) {
        return null;
      }
      // `<input type="datetime-local">` returns a local-time string
      // with no zone (e.g. "2026-05-12T09:00"). Constructing `new
      // Date(...)` interprets it as local-time, which matches the
      // user's expectation when they pick "09:00" on the picker.
      return new Date(customIso);
  }
}

export function SnoozeDialog({
  opened,
  onClose,
  onConfirm,
  itemTitle,
}: Readonly<SnoozeDialogProps>) {
  const [preset, setPreset] = useState<SnoozePreset>('1-hour');
  const [customIso, setCustomIso] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wakeAt = useMemo(() => wakeAtForPreset(preset, customIso), [preset, customIso]);
  // Future-date guard — the backend rejects past wake_at with 400, so
  // we surface the same constraint client-side rather than waiting
  // for the round trip.
  const isFuture = wakeAt !== null && wakeAt.getTime() > Date.now();
  const canSubmit = isFuture && !submitting;

  async function handleConfirm() {
    if (!wakeAt) {
      setError('Pick a snooze time.');
      return;
    }
    if (!isFuture) {
      setError('Snooze time must be in the future.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(wakeAt, reason.trim() ? reason.trim() : null);
      // Reset only on success; on failure the user usually wants to
      // retry with the same values.
      setPreset('1-hour');
      setCustomIso('');
      setReason('');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (!submitting) {
          onClose();
        }
      }}
      title="Snooze item"
      centered
      size="md"
    >
      <Stack gap="sm">
        {itemTitle && (
          <Text size="sm" c="dimmed">
            {itemTitle}
          </Text>
        )}
        <Radio.Group
          value={preset}
          onChange={(v) => setPreset(v as SnoozePreset)}
          label="Wake me when"
        >
          <Stack gap="xs" mt="xs">
            <Radio value="1-hour" label="In 1 hour" />
            <Radio value="tomorrow-morning" label="Tomorrow morning (09:00)" />
            <Radio value="next-week" label="Next Monday (09:00)" />
            <Radio value="custom" label="Custom" />
          </Stack>
        </Radio.Group>
        {preset === 'custom' && (
          <TextInput
            type="datetime-local"
            label="Wake at"
            value={customIso}
            onChange={(e) => setCustomIso(e.currentTarget.value)}
            placeholder="YYYY-MM-DDThh:mm"
            data-testid="snooze-custom-datetime"
          />
        )}
        <TextInput
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          placeholder="Why snooze?"
        />
        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canSubmit} loading={submitting}>
            Snooze
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
